/**
 * Panel Picker — dropdown checklist for toggling panels on/off.
 * Gear icon in the bottom panel tab bar opens this popover.
 * Changes persist to localStorage, overriding desk preset defaults.
 */

import { PANEL_REGISTRY, type PanelDef, type PanelCategory } from '@/config/panels';
import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { panelManager } from '@/panels/panel-manager';
import { getDeskById } from '@/config/desk-presets';
import { createDeskPresetEditor, getAnyPresetById } from '@/ui/components/desk-preset-editor';

const CATEGORY_LABELS: Record<PanelCategory, string> = {
  intelligence: 'Intelligence',
  geopolitical: 'Geopolitical',
  infrastructure: 'Infrastructure',
  finance: 'Finance',
  environmental: 'Environmental',
  tools: 'Tools',
  settings: 'Settings',
};

const CATEGORY_ORDER: PanelCategory[] = [
  'intelligence', 'geopolitical', 'infrastructure', 'finance',
  'environmental', 'tools', 'settings',
];

// ── Persistence ──

function getEnabledKey(deskId: string): string {
  return `sentinel-panel-enabled-${deskId}`;
}

/** Load user panel overrides for a desk. Returns null if no overrides saved. */
export function loadPanelOverrides(deskId: string): Record<string, boolean> | null {
  try {
    const raw = localStorage.getItem(getEnabledKey(deskId));
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, boolean>;
  } catch { return null; }
}

/** Save user panel overrides for a desk. */
function savePanelOverrides(deskId: string, overrides: Record<string, boolean>): void {
  try {
    localStorage.setItem(getEnabledKey(deskId), JSON.stringify(overrides));
  } catch { /* ignore */ }
}

/** Compute the effective enabled panel set for a desk, merging preset defaults with user overrides. */
export function computeEnabledPanels(deskId: string): Set<string> {
  // Check built-in presets first, then custom presets
  const desk = getDeskById(deskId);
  let presetIds: Set<string>;
  if (desk) {
    presetIds = new Set(desk.enabledPanels);
  } else {
    // May be a custom preset — try to load from localStorage
    try {
      const customRaw = localStorage.getItem('sentinel-custom-presets');
      if (customRaw) {
        const customs = JSON.parse(customRaw) as { id: string; enabledPanels: string[] }[];
        const custom = customs.find(c => c.id === deskId);
        if (custom) {
          presetIds = new Set(custom.enabledPanels);
        } else {
          presetIds = new Set(PANEL_REGISTRY.filter(p => p.defaultEnabled).map(p => p.id));
        }
      } else {
        presetIds = new Set(PANEL_REGISTRY.filter(p => p.defaultEnabled).map(p => p.id));
      }
    } catch {
      presetIds = new Set(PANEL_REGISTRY.filter(p => p.defaultEnabled).map(p => p.id));
    }
  }
  const overrides = loadPanelOverrides(deskId);
  if (!overrides) return presetIds;

  const result = new Set(presetIds);
  for (const [id, enabled] of Object.entries(overrides)) {
    if (enabled) {
      result.add(id);
    } else {
      result.delete(id);
    }
  }
  return result;
}

/** Apply the enabled panel set to the panel manager. */
export function applyPanelOverrides(deskId: string): void {
  const enabled = computeEnabledPanels(deskId);
  panelManager.setEnabledPanels(enabled);
}

// ── Panel Picker UI ──

export function createPanelPicker(): { element: HTMLElement; destroy: () => void } {
  const container = document.createElement('div');
  container.className = 'panel-picker';

  const btn = document.createElement('button');
  btn.className = 'panel-picker-btn';
  btn.title = 'Configure panels';
  btn.setAttribute('aria-label', 'Configure panels');
  btn.innerHTML = `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M8 10a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" stroke-width="1.5"/>
    <path d="M13.7 6.3l-1-.2a5 5 0 00-.4-1l.6-.8a.5.5 0 00-.1-.6l-1.1-1a.5.5 0 00-.6 0l-.8.5a5 5 0 00-1-.4l-.2-1a.5.5 0 00-.5-.4H7.1a.5.5 0 00-.5.4l-.2 1a5 5 0 00-1 .4l-.8-.5a.5.5 0 00-.6 0l-1 1a.5.5 0 00-.1.6l.6.8a5 5 0 00-.4 1l-1 .2a.5.5 0 00-.4.5v1.4a.5.5 0 00.4.5l1 .2a5 5 0 00.4 1l-.6.8a.5.5 0 00.1.6l1.1 1a.5.5 0 00.6 0l.8-.5a5 5 0 001 .4l.2 1a.5.5 0 00.5.4h1.4a.5.5 0 00.5-.4l.2-1a5 5 0 001-.4l.8.5a.5.5 0 00.6 0l1-1a.5.5 0 00.1-.6l-.6-.8a5 5 0 00.4-1l1-.2a.5.5 0 00.4-.5V6.8a.5.5 0 00-.4-.5z" stroke="currentColor" stroke-width="1.2"/>
  </svg>`;
  container.appendChild(btn);

  const dropdown = document.createElement('div');
  dropdown.className = 'panel-picker-dropdown';
  dropdown.style.display = 'none';
  container.appendChild(dropdown);

  let isOpen = false;

  function buildDropdownContent(): void {
    dropdown.innerHTML = '';

    const deskId = appStore.getState().variant;
    const enabled = computeEnabledPanels(deskId);

    // Group panels by category
    const groups = new Map<PanelCategory, PanelDef[]>();
    for (const panel of PANEL_REGISTRY) {
      const list = groups.get(panel.category) ?? [];
      list.push(panel);
      groups.set(panel.category, list);
    }

    // Header
    const header = document.createElement('div');
    header.className = 'panel-picker-header';
    header.textContent = 'Panels';
    dropdown.appendChild(header);

    // Scrollable body
    const body = document.createElement('div');
    body.className = 'panel-picker-body';

    for (const category of CATEGORY_ORDER) {
      const panels = groups.get(category);
      if (!panels || panels.length === 0) continue;

      const section = document.createElement('div');
      section.className = 'panel-picker-section';

      const catLabel = document.createElement('div');
      catLabel.className = 'panel-picker-category';
      catLabel.textContent = CATEGORY_LABELS[category];
      section.appendChild(catLabel);

      for (const panel of panels) {
        const row = document.createElement('label');
        row.className = 'panel-picker-row';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'panel-picker-checkbox';
        checkbox.checked = enabled.has(panel.id);
        checkbox.onchange = () => {
          togglePanelEnabled(panel.id, checkbox.checked);
        };
        row.appendChild(checkbox);

        const info = document.createElement('div');
        info.className = 'panel-picker-info';

        const name = document.createElement('span');
        name.className = 'panel-picker-name';
        name.textContent = panel.name;
        info.appendChild(name);

        const desc = document.createElement('span');
        desc.className = 'panel-picker-desc';
        desc.textContent = panel.description;
        info.appendChild(desc);

        row.appendChild(info);
        section.appendChild(row);
      }

      body.appendChild(section);
    }

    dropdown.appendChild(body);

    // Footer with reset button
    const footer = document.createElement('div');
    footer.className = 'panel-picker-footer';

    const resetBtn = document.createElement('button');
    resetBtn.className = 'panel-picker-reset';
    resetBtn.textContent = 'Reset to desk defaults';
    resetBtn.onclick = () => {
      const currentDesk = appStore.getState().variant;
      try { localStorage.removeItem(getEnabledKey(currentDesk)); } catch { /* ignore */ }
      applyPanelOverrides(currentDesk);
      buildDropdownContent();
    };
    footer.appendChild(resetBtn);
    dropdown.appendChild(footer);

    // Desk Preset Editor section (WP-10.5)
    const { element: presetEditorEl } = createDeskPresetEditor();
    dropdown.appendChild(presetEditorEl);
  }

  function togglePanelEnabled(panelId: string, enabled: boolean): void {
    const deskId = appStore.getState().variant;
    const desk = getAnyPresetById(deskId);
    const presetDefault = desk ? desk.enabledPanels.includes(panelId) : (PANEL_REGISTRY.find(p => p.id === panelId)?.defaultEnabled ?? false);

    const overrides = loadPanelOverrides(deskId) ?? {};

    // Only store override if it differs from preset default
    if (enabled === presetDefault) {
      delete overrides[panelId];
    } else {
      overrides[panelId] = enabled;
    }

    // Clean up empty overrides
    if (Object.keys(overrides).length === 0) {
      try { localStorage.removeItem(getEnabledKey(deskId)); } catch { /* ignore */ }
    } else {
      savePanelOverrides(deskId, overrides);
    }

    applyPanelOverrides(deskId);
  }

  function open(): void {
    if (isOpen) return;
    isOpen = true;
    buildDropdownContent();
    dropdown.style.display = 'flex';
    requestAnimationFrame(() => dropdown.classList.add('visible'));
  }

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    dropdown.classList.remove('visible');
    setTimeout(() => { dropdown.style.display = 'none'; }, 150);
  }

  btn.onclick = (e) => {
    e.stopPropagation();
    if (isOpen) close(); else open();
  };

  // Close on outside click
  function onDocClick(e: MouseEvent): void {
    if (isOpen && !container.contains(e.target as Node)) {
      close();
    }
  }
  document.addEventListener('click', onDocClick);

  // Close and rebuild when desk changes
  const unsub = eventBus.on('variant:changed', () => {
    close();
  });

  function destroy(): void {
    document.removeEventListener('click', onDocClick);
    unsub();
    container.remove();
  }

  return { element: container, destroy };
}
